import { formatInches } from '../lib/format.js';

interface Field {
  label: string;
  value: number;
  unit?: 'in' | 'deg' | 'count';
  primary?: boolean;
}

interface Props {
  fields: Field[];
}

export function ResultBlock({ fields }: Props) {
  if (fields.length === 0) return null;

  const hero = fields.find((field) => field.primary) ?? fields[0]!;
  const supporting = fields.filter((field) => field !== hero);

  return (
    <section
      aria-label="Calculated result"
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-5)',
        marginTop: 'var(--space-5)',
        display: 'grid',
        gap: 'var(--space-4)',
      }}
    >
      <ResultField field={hero} hero />
      {supporting.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))',
            gap: 'var(--space-3)',
          }}
        >
          {supporting.map((field) => (
            <ResultField key={field.label} field={field} />
          ))}
        </div>
      )}
    </section>
  );
}

function ResultField({ field, hero = false }: { field: Field; hero?: boolean }) {
  const value = renderValue(field);
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: hero ? 'var(--space-2)' : 'var(--space-1)',
        }}
      >
        {field.label}
      </div>
      <div
        className="mono"
        style={{
          fontSize: hero ? 'clamp(2.35rem, 10vw, 4rem)' : 'var(--text-xl)',
          color: hero ? 'var(--color-accent)' : 'var(--color-text-primary)',
          fontWeight: hero ? 800 : 650,
          lineHeight: 1,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function renderValue(f: Field): string {
  if (f.unit === 'deg') return `${f.value.toFixed(1)}\u00b0`;
  if (f.unit === 'count') return String(Math.round(f.value));
  return formatInches(f.value);
}
